import dayjs from 'dayjs'
import {supabase} from './BackendService'
import {
  CustomJournalEventType,
  CustomType,
  JournalEventType,
  JournalRecord,
} from '../entities/journalRecord'
import {PostgrestError} from '@supabase/supabase-js'
import {createCustomTypeFilter} from './queryUtils'

export type JournalFilters = {
  // string is a predefined event type, number means custom event type with this id
  eventType?: (string | number)[]
  actualCompletionDateUtc?: {startDate: Date; endDate: Date}
  plant?: string
  project?: string[]
}

export const journalEventDefaultQuery = `id,creationDateUtc,startDateUtc,endDateUtc,comment,plant:plantId (id, name), plantFilter:plantId(name), deletedPlantName,project:projectId (id, name),deletedProjectName, type, customEventType:customEventTypes(id,name,icon), actualCompletionDateUtc`

export function transformEvent(databaseEventRecord: any) {
  const {type, customEventType, ...rest} = databaseEventRecord

  return {
    id: databaseEventRecord.id,
    startDateUtc: databaseEventRecord.startDateUtc,
    eventId: databaseEventRecord.id,
    plant: databaseEventRecord.deletedPlantName
      ? {id: null, name: databaseEventRecord.deletedPlantName}
      : Array.isArray(databaseEventRecord.plant)
      ? databaseEventRecord.plant[0]
      : databaseEventRecord.plant,
    project: databaseEventRecord.deletedProjectName
      ? {id: null, name: databaseEventRecord.deletedProjectName}
      : Array.isArray(databaseEventRecord.project)
      ? databaseEventRecord.project[0]
      : databaseEventRecord.project,
    creationDateUtc: databaseEventRecord.creationDateUtc,
    actualCompletionDateUtc: databaseEventRecord.actualCompletionDateUtc,
    type: type === 'custom' ? {type: type, customJournalEventType: customEventType} : {type: type},
    comment: databaseEventRecord.comment,
  }

  // if (type === 'custom') {
  //   return {
  //     ...databaseEventRecord,
  //     type: {
  //       type,
  //       customEventType,
  //     },
  //   }
  // } else {
  //   return {
  //     ...databaseEventRecord,
  //     type: {
  //       type: type,
  //     },
  //   }
  // }
}

export class JournalService {
  public async getUserJournalRecords({
    page,
    perPage,
    filters,
    sort,
  }: {
    page: number
    perPage: number
    filters: JournalFilters
    sort?: {field: string; direction: 'asc' | 'desc'}
  }): Promise<{data: JournalRecord[]; count: number}> {
    let query = supabase.from('journalEvents').select(
      journalEventDefaultQuery,

      {count: 'exact'}
    )

    // if (filters.eventCategory) {
    //   query = query.filter('eventCategory', 'eq', filters.eventCategory)
    // }

    const eventTypeFilter = createCustomTypeFilter(filters.eventType)

    if (eventTypeFilter.length > 0) {
      query = query.or(eventTypeFilter.join(','))
    }
    if (filters.project) {
      query = query.filter('projectId', 'in', `(${filters.project.map((e) => `"${e}"`).join(',')})`)
    }
    if (filters.plant) {
      query = query
        .ilike('plantFilter.name', `%${filters.plant}%`)
        .or(`plantFilter.not.is.null,deletedPlantName.ilike.%${filters.plant}%`)

      // query = query.or(
      //   // `deletedPlantName.ilike.%${filters.plant}%`
      //   `plant.name.ilike.%${filters.plant}%`
      //   //`plant.name.ilike.%${filters.plant}%,deletedPlantName.ilike.%${filters.plant}%`
      // )
    }
    if (
      filters.actualCompletionDateUtc &&
      filters.actualCompletionDateUtc.startDate &&
      !filters.actualCompletionDateUtc.endDate
    ) {
      query = query.filter(
        'actualCompletionDateUtc',
        'gte',
        dayjs(filters.actualCompletionDateUtc.startDate).format('YYYY-MM-DD')
      )
    }
    if (
      filters.actualCompletionDateUtc &&
      filters.actualCompletionDateUtc.endDate &&
      !filters.actualCompletionDateUtc.startDate
    ) {
      query = query.filter(
        'actualCompletionDateUtc',
        'lte',
        dayjs(filters.actualCompletionDateUtc.endDate).format('YYYY-MM-DD')
      )
    }
    if (
      filters.actualCompletionDateUtc &&
      filters.actualCompletionDateUtc.endDate &&
      filters.actualCompletionDateUtc.startDate
    ) {
      query = query
        .filter(
          'actualCompletionDateUtc',
          'lte',
          dayjs(filters.actualCompletionDateUtc.endDate).format('YYYY-MM-DD')
        )
        .filter(
          'actualCompletionDateUtc',
          'gte',
          dayjs(filters.actualCompletionDateUtc.startDate).format('YYYY-MM-DD')
        )
    }

    if (sort && sort.field === 'plant') {
      query = query.order('plant(name)', {ascending: sort.direction === 'asc'})
    } else if (sort && sort.field !== 'plant') {
      query = query.order(sort.field, {ascending: sort.direction === 'asc'})
    } else {
      query = query.order('startDateUtc', {ascending: false})
    }

    query = query.range((page - 1) * perPage, page * perPage - 1)

    const {data: plantEvents, count, error} = await query

    if (error) {
      throw error
    }

    // return toDoEvents.map((e) => ({
    //   eventCategory: 'finishedToDo',
    //   date: e.targetStartDateUtc,
    //   eventId: e.id,
    //   name: e.name,
    // }))

    return {
      data: plantEvents.map((e) => ({
        id: e.id,
        startDateUtc: e.startDateUtc,
        eventId: e.id,
        plant: e.deletedPlantName
          ? {id: null, name: e.deletedPlantName}
          : Array.isArray(e.plant)
          ? e.plant[0]
          : e.plant,
        project: e.deletedProjectName
          ? {id: null, name: e.deletedProjectName}
          : Array.isArray(e.project)
          ? e.project[0]
          : e.project,
        creationDateUtc: e.creationDateUtc,
        actualCompletionDateUtc: e.actualCompletionDateUtc,
        type:
          e.type === 'custom'
            ? {type: 'custom', customJournalEventType: e.customEventType}
            : {type: e.type},
        comment: e.comment,
      })) as JournalRecord[],
      count,
    }
  }

  public async getUserJournalRecordById(id: number) {
    const {data, error} = await supabase
      .from('journalEvents')
      .select(journalEventDefaultQuery)
      .eq('id', id)

    if (error) {
      return {success: false, error}
    } else if (data.length === 0) {
      return {success: false, error: 'notFound'}
    } else {
      return {
        success: true,
        data: transformEvent(data[0]),
      }
    }
  }
  public async getUserJournalEventTypes() {
    const {data, error} = await supabase
      .from('customEventTypes')
      .select('id, name, icon')
      .eq('owner_id', (await supabase.auth.getSession()).data.session?.user.id)

    if (error) {
      return {success: false, error}
    } else {
      return {
        success: true,
        data: data.map((type) => ({
          type: 'custom',
          customJournalEventType: type,
        })) as CustomJournalEventType[],
      }
    }
  }

  public async createUserJournalEventType(
    name: string,
    icon: string
  ): Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customEventTypes')
      .insert([
        {
          name: name,
          icon: icon,
          owner_id: (await supabase.auth.getSession()).data.session?.user.id,
        },
      ])
      .select('id, name, icon')

    if (error) {
      return {success: false, error}
    } else {
      return {success: true, data: data[0] as CustomType}
    }
  }

  public async editUserJournalEventType(newType: CustomType): Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customEventTypes')
      .update({name: newType.name, icon: newType.icon})
      .eq('id', newType.id)
      .eq('owner_id', (await supabase.auth.getSession()).data.session?.user.id)
      .select('id, name, icon')

    if (error) {
      return {success: false, error}
    } else {
      return {success: true, data: data[0]}
    }
  }

  public async deleteUserJournalEventType(id: number): Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  > {
    const {data, error} = await supabase
      .from('customEventTypes')
      .delete()
      .eq('id', id)
      .eq('owner_id', (await supabase.auth.getSession()).data.session?.user.id)

    if (error) {
      return {success: false, error}
    } else {
      return {success: true, data}
    }
  }

  public async getJournalEventsByPlantId(plantId: number): Promise<JournalRecord[]> {
    let {data: plantTimeEvents, error} = await supabase
      .from('journalEvents')
      .select(journalEventDefaultQuery)
      .eq('plantId', plantId)

    return plantTimeEvents.map((event: any) => ({
      id: event.id,
      creationDateUtc: event.creationDateUtc,
      startDateUtc: event.startDateUtc,
      endDateUtc: event.endDateUtc,
      actualCompletionDateUtc: event.actualCompletionDateUtc,
      plant: {
        id: event.plant.id,
        name: event.plant.id ? event.plant.name : event.deletedPlantName,
      },
      //photos: event.photos,
      comment: event.comment,
      type:
        event.type === 'custom'
          ? {type: 'custom', customJournalEventType: event.customEventType}
          : {type: event.type},
    })) as JournalRecord[]
  }

  public async createJournalEvent(
    newEvent: Omit<JournalRecord, 'id'>,
    newPhotos: [] = []
  ): Promise<{success: true; data: CustomType} | {success: false; error}> {
    const userId = (await supabase.auth.getSession()).data.session?.user.id

    // if (newPhotos) {
    //   let result = await this.addPlantEventPhotos(newPhotos, userId, newPlantEvent.plant.id)

    //   if (result.success === false) {
    //     return {success: false, error: result.errors.map((e) => e.message).join(', ')}
    //   } else {
    //     newPlantEvent.photos = result.photos
    //   }
    // }

    const {data, error} = await supabase.from('journalEvents').insert([
      {
        creationDateUtc: dayjs(Date.now()).format('YYYY-MM-DD'),
        plantId: newEvent.plant.id,
        //photos: newPlantEvent.photos,
        startDateUtc: newEvent.startDateUtc,
        endDateUtc: newEvent.endDateUtc,
        comment: newEvent.comment,
        ownerId: userId,
        type: newEvent.type.type,
        custom_type_id:
          newEvent.type.type === 'custom' ? newEvent.type.customJournalEventType.id : null,
        actualCompletionDateUtc: newEvent.actualCompletionDateUtc,
      },
    ])

    if (error) {
      return {success: false, error: error.message}
    } else {
      return {success: true, data}
    }
  }

  public async editJournalEvent(
    currentJournalEvent: JournalRecord,
    newPhotos: [] = [],
    photos?: string[]
  ) {
    // if (photos && photos.length < currentPlantEvent.photos.length) {
    //   let deletedPhotos = currentPlantEvent.photos.map((photo, index) => {
    //     if (!photos.includes(photo)) {
    //       return photo
    //     }
    //   })
    //   let result = await this.deletePlantEventPhotos(
    //     deletedPhotos,
    //     userId,
    //     currentPlantEvent.plant.id
    //   )

    //   if (result.success === false) {
    //     return {success: false, error: result.error.message}
    //   } else {
    //     currentPlantEvent.photos = photos
    //   }
    // }

    // if (newPhotos) {
    //   let result = await this.addPlantEventPhotos(newPhotos, userId, currentPlantEvent.plant.id)

    //   if (result.success === false) {
    //     return {success: false, error: result.errors.map((e) => e.message).join(', ')}
    //   } else {
    //     currentPlantEvent.photos = [...(currentPlantEvent?.photos || []), ...result.photos]
    //   }
    // }

    const {plant, type, ...journalEvent} = currentJournalEvent

    const {data, error} = await supabase
      .from('journalEvents')
      .update([
        {
          ...journalEvent,
          plantId: plant.id,
          type: type.type,
          custom_type_id: type.type === 'custom' ? type.customJournalEventType.id : null,
        },
      ])
      .eq('id', currentJournalEvent.id)

    if (error) {
      return {success: false, error: error.message}
    } else {
      return {success: true, data}
    }
  }

  public async deleteJournalEvent(currentEvent: JournalRecord) {
    // if (currentEvent.photos.length > 0) {
    //   let result = await this.deletePlantEventPhotos(
    //     currentEvent.photos,
    //     (
    //       await supabase.auth.getSession()
    //     ).data.session.user.id,
    //     currentEvent.plant.id
    //   )

    //   if (result.success === false) {
    //     return {success: false, error: result.error.message}
    //   }
    // }
    const {data, error} = await supabase.from('journalEvents').delete().eq('id', currentEvent.id)
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }
}

export const instance = new JournalService()
